import { useState, useCallback, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import IntroLoader from './components/IntroLoader'
import ChatWidget from './components/ChatWidget'
import CallWidget from './components/CallWidget/CallWidget'
import ScrollToTopButton from './components/ScrollToTopButton/ScrollToTopButton'
import ScrollToTop from './components/ScrollToTop'
import RouteFallback from './components/RouteFallback'
import CookieConsent from './components/CookieConsent/CookieConsent'
import AnnouncementBanner from './components/AnnouncementBanner'

// Cookie consent banner is hidden for now. Set to true to show it again.
const SHOW_COOKIE_CONSENT = false
import usePageTracking from './hooks/usePageTracking'
import { prefetchPosts } from './utils/blogApi'

// Homepage and Proof are now lazy-loaded below to reduce initial CSS size
import './App.css'

// Lazy load pages for better performance
const Homepage = lazy(() => import('./pages/Homepage'))
const Proof = lazy(() => import('./pages/Proof'))
const Services = lazy(() => import('./pages/Services'))
const Geospatial = lazy(() => import('./pages/Geospatial'))
const CloudPlatforms = lazy(() => import('./pages/CloudPlatforms'))
const AIAutomation = lazy(() => import('./pages/AIAutomation'))
const DataAnalytics = lazy(() => import('./pages/DataAnalytics'))
const Solutions = lazy(() => import('./pages/Solutions'))
const Insights = lazy(() => import('./pages/Insights'))
const InsightDetail = lazy(() => import('./pages/InsightDetail'))
const About = lazy(() => import('./pages/About'))
const Team = lazy(() => import('./pages/Team'))
const Careers = lazy(() => import('./pages/Careers'))
const CareerDetail = lazy(() => import('./pages/CareerDetail'))
const Contact = lazy(() => import('./pages/Contact'))
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'))
const Privacy = lazy(() => import('./pages/Privacy'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Terms = lazy(() => import('./pages/Terms'))
const Cookies = lazy(() => import('./pages/Cookies'))
const CSR = lazy(() => import('./pages/CSR'))
const DataStore = lazy(() => import('./pages/DataStore'))
const DataProtection = lazy(() => import('./pages/DataProtection'))

function PageTracker() {
  usePageTracking()
  return null
}

function App() {
  const [introComplete, setIntroComplete] = useState(false)

  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true)
    // Pre-warm the blog cache in the background so Insights loads instantly
    prefetchPosts()

    // Prefetch main pages in the background so navigation is instant when clicked
    setTimeout(() => {
      import('./pages/Homepage')
      import('./pages/Services')
      import('./pages/Solutions')
      import('./pages/Proof')
      import('./pages/DataStore')
      import('./pages/CSR')
      import('./pages/About')
      import('./pages/Team')
      import('./pages/Careers')
      import('./pages/Contact')
      import('./pages/Insights')
    }, 1000)
  }, [])

  return (
    <BrowserRouter>
      {!introComplete && <IntroLoader onComplete={handleIntroComplete} />}
      {introComplete && (
        <>
          <ScrollToTop />
          <PageTracker />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/services" element={<Services />} />
              <Route path="/geospatial" element={<Geospatial />} />
              <Route path="/cloud-platforms" element={<CloudPlatforms />} />
              <Route path="/ai-automation" element={<AIAutomation />} />
              <Route path="/data-analytics" element={<DataAnalytics />} />
              <Route path="/proof" element={<Proof />} />
              <Route path="/projects" element={<Proof />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/:slug" element={<ProjectDetail />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/:slug" element={<CareerDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/csr" element={<CSR />} />
              <Route path="/gdpr" element={<DataProtection />} />
              <Route path="/data-store" element={<DataStore />} />
            </Routes>
          </Suspense>
          <CallWidget />
          <ChatWidget />
          <ScrollToTopButton />
          {SHOW_COOKIE_CONSENT && <CookieConsent />}
          <AnnouncementBanner />
        </>
      )}
    </BrowserRouter>
  )
}

export default App
